import { Container, IDestroyOptions } from "@pixi/display";
import { GameModel } from "../../../models/game/GameModel";
import { Sprite } from "@pixi/sprite";
import { CellViewFactory } from "../cells/CellViewFactory";
import { TriangleViewFactory } from "../triangles/TriangleViewFactory";
import { CellView } from "../cells/CellView";
import { TriangleView } from "../triangles/TriangleView";
import { Point } from "@pixi/core";
import { FigureViewFactory } from "../figures/FigureViewFactory";
import { ShadowFigureView } from "../figures/ShadowFigureView";
import { Spine } from "pixi-spine";
import { GameViewController } from "../../../controllers/GameViewController";
import { Localization } from "../../../../common/Localization";
import { AudioManager } from "../../../../../audio/AudioManager";
import { Text, TextStyle } from "@pixi/text";
import { getDefaultFontFamily } from "../../../../common/TextUtils";
import { gsap } from "gsap";
import { Timer } from "../../tools/Timer";
import { ComboView } from "./ComboView";
import { PlayerModel } from "../../../models/player/PlayerModel";
import { GameUIResourcesPackage } from "../../../../preloader/resources/GameUIResourcesPackage";

export class GamefieldView extends Container {
    private field: Sprite;
    private cellViews: Array<CellView>;
    private shadowFigure: ShadowFigureView;
    private cellsForDestroy: Array<CellView>;
    private comboView: ComboView;
    private _isPlayAnimations: boolean;

    public get isPlayAnimations(): boolean {
        return this._isPlayAnimations;
    }
    public readonly borderOffset = 4;
    public readonly distanceBetweenCells = 4;
    public constructor(
        private readonly gameUIResourcesPackage: GameUIResourcesPackage,
        private readonly cellViewFactory: CellViewFactory,
        private readonly triangleViewFactory: TriangleViewFactory,
        private readonly figureViewFactory: FigureViewFactory,
        private readonly playerModel: PlayerModel,
        private readonly gameModel: GameModel,
        private readonly gameViewController: GameViewController,
        private readonly localization: Localization,
        private readonly audioManager: AudioManager
    ) {
        super();
        this.cellViews = new Array<CellView>();
        this.cellsForDestroy = new Array<CellView>();
        this._isPlayAnimations = false;
        this.build();
        this.gameModel.onChanged.add(this.update, this);
        this.gameModel.deckModel.onSelectFigure.add(this.updateLightFigure, this);
        this.gameModel.onGameOver.add(this.handleGameOver, this);
        this.gameModel.onReject.add(this.hideShadowFigure, this);
    }

    private async build(): Promise<void> {
        this.field = this.addChild(new Sprite(this.gameUIResourcesPackage.assets_game_gamefield_png));
        for (let i = 0; i < this.gameModel.GamefieldHeight; i++) {
            for (let j = 0; j < this.gameModel.GamefieldWidth; j++) {
                const dataIndex = i * this.gameModel.GamefieldWidth + j;
                const cellData = this.gameModel.Gamefield[dataIndex];
                const firstTriangle = cellData.FigureInside.Triangles[0];
                const secondTriangle = cellData.FigureInside.Triangles[1];
                const cell = this.cellViewFactory.createCellView(
                    firstTriangle != null ? this.triangleViewFactory.createTriangleView(firstTriangle.TriangleRotationStatus, firstTriangle.Color) : null,
                    secondTriangle != null ? this.triangleViewFactory.createTriangleView(secondTriangle.TriangleRotationStatus, secondTriangle.Color) : null
                );
                this.addChild(cell)
                    .position.set(
                        this.borderOffset + (this.distanceBetweenCells + cell.width) * j,
                        this.borderOffset + (this.distanceBetweenCells + cell.height) * i
                    );
                this.cellViews.push(cell);
            }
        }
        await this.initializeAnimation();
        this.comboView = new ComboView(this.gameUIResourcesPackage, this.audioManager, this.playerModel, this.gameModel);
        this.comboView.position.set(this.field.width * 0.5, this.field.height * 0.5);
        this.addChild(this.comboView);
    }

    private async initializeAnimation(): Promise<void> {
        const triangles = this.cellViews.map(cell => cell.TriangleViews).flat();
        const animations = triangles.map(triangle => triangle.createAnimation());
        await Promise.all(animations);
    }

    private async handleGameOver(_isHaveShuffles: boolean): Promise<void> {
        const showAnimation = new gsap.core.Timeline({ paused: true });

        const effect = new Spine((this.gameUIResourcesPackage.assets_game_effects_game_over_Great_animation_json as any).spineData);
        effect.position.set((this.width - effect.width) * 0.5, (this.height - effect.height) * 0.5);
        effect.state.setAnimation(0, "Great_animation", true);
        this.addChild(effect);
        await new Timer(650);

        const message = new Text(this.localization.getText("great"), new GreatTextStyle());
        message.anchor.set(0.5);
        message.scale.set(0);
        effect.addChild(message);
        showAnimation.fromTo(message.scale, { x: 0, y: 0 }, { x: 1, y: 1, ease: "none", duration: 0.15 });
        await showAnimation.play();
        showAnimation.kill();
        await new Timer(2100);
        this.removeChild(effect).destroy();
        this.gameViewController.showGameOverScene();
    }

    private updateLightFigure(): void {
        if (this.shadowFigure != null) {
            this.removeChild(this.shadowFigure).destroy({ children: true });
        }
        this.shadowFigure = this.figureViewFactory.createShadowFigure(
            this.gameModel.deckModel.deck[this.gameModel.deckModel.SelectedFigureIndex]
        );
        this.addChild(this.shadowFigure);
        this.shadowFigure.visible = false;
    }

    private update(): void {
        this.cellsForDestroy.length = 0;
        for (let i = 0; i < this.gameModel.Gamefield.length; i++) {
            const cell = this.cellViews[i];
            const cellData = this.gameModel.Gamefield[i];
            const trianglesToUpdate: TriangleView[] = [];
            if (cellData.eraseTriangleIndexWithAnimation != -1) {
                cell.eraseTriangleIndexWithAnimation = cellData.eraseTriangleIndexWithAnimation;
            }
            for (let j = 0; j < 2; j++) {
                const triangleData = cellData.FigureInside.Triangles[j];
                const existingTriangle = cell.TriangleViews[j];
                if (triangleData == null) {
                    if (cellData.FigureInside.Triangles[0] == null &&
                        cellData.FigureInside.Triangles[1] == null &&
                        !cell.isEmpty
                    ) {
                        this.cellsForDestroy.push(cell);
                        break;
                    }
                } else if (existingTriangle == null ||
                    existingTriangle.Color !== triangleData.Color ||
                    existingTriangle.RotationStatus !== triangleData.TriangleRotationStatus) {
                    trianglesToUpdate.push(this.triangleViewFactory.createTriangleView(
                        triangleData.TriangleRotationStatus,
                        triangleData.Color
                    ));
                } else {
                    trianglesToUpdate.push(existingTriangle);
                }
            }
            if (trianglesToUpdate.length > 0) {
                cell.updateCellView(trianglesToUpdate);
            }
            cellData.eraseTriangleIndexWithAnimation = -1;
        }
        this.hideShadowFigure();
        if (this.cellsForDestroy.length > 0) {
            this.destroyTriangles();
        }
    }

    public getNearestCellIndex(position: Point): number {
        if (this._isPlayAnimations) {
            this.shadowFigure.visible = false;
            return -1;
        }
        for (let i = 0; i < this.cellViews.length; i++) {
            const bounds = this.cellViews[i].getBounds();
            if (bounds.contains(position.x, position.y)) {
                this.shadowFigure.visible = true;
                this.shadowFigure.position = this.cellViews[i].position;
                return i;
            }
        }
        this.shadowFigure.visible = false;
        return -1;
    }

    private async destroyTriangles(): Promise<void> {
        this._isPlayAnimations = true;
        let animations = new Array<Promise<void>>();
        for (const cell of this.cellsForDestroy) {
            for (let i = 0; i < cell.TriangleViews.length; i++) {
                animations.push(cell.destroyTriangle(i));
            }
        }
        await Promise.all(animations);
        this._isPlayAnimations = false;
    }

    private hideShadowFigure(): void {
        if (this.shadowFigure != null) {
            this.shadowFigure.visible = false;
        }
    }

    public override destroy(options?: IDestroyOptions | boolean): void {
        this.gameModel.onReject.remove(this.hideShadowFigure, this);
        this.gameModel.onChanged.remove(this.update, this);
        super.destroy(options);
    }
}

class GreatTextStyle extends TextStyle {
    constructor() {
        super({
            fontFamily: getDefaultFontFamily(),
            fontSize: 128,
            fill: "#ffffff",
            stroke: "#28226E",
            strokeThickness: 4,
            lineJoin: "round",
            align: "center"
        });
    }
}
